export const SPLIT_SUM_TOLERANCE = 0.01;

// PERS2-79: время операции обязательно; формат 'YYYY-MM-DD HH:mm' или
// 'YYYY-MM-DDTHH:mm' (24-часовой). Date-only значения не проходят.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
// 24-часовой формат: часы 00-23, минуты 00-59 (PERS2-79 AC-1).
const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}[T ](?:[01]\d|2[0-3]):[0-5]\d$/;

export const splitTotal = (splits = []) => (splits || []).reduce(
  (sum, split) => sum + (Number(split.amount) || 0),
  0
);

// Валидация разбивки операции на части (PERS2-78, PERS2-83). Возвращает ошибку
// поля «splits» или пустой объект, если разбивка корректна / отсутствует.
// categories — справочник категорий пользователя ([{ id, parent_id }]):
// при непустых splits заголовочная категория обязательна, и каждая часть
// должна быть прямой дочерней категорией основной категории (PERS2-83).
export const splitValidationErrors = (formData = {}, categories = []) => {
  const errors = {};
  const splits = Array.isArray(formData.splits) ? formData.splits : [];
  if (!splits.length) return errors;

  if (formData.type !== 'expense') {
    errors.splits = 'Разбивка доступна только для расходов';
    return errors;
  }
  if (splits.length < 2) {
    errors.splits = 'Добавьте минимум 2 части разбивки или удалите разбивку';
    return errors;
  }
  // PERS2-83 AC-1: при разбивке заголовочная категория обязательна.
  if (!formData.category_id) {
    errors.splits = 'При разбивке укажите основную категорию расхода';
    return errors;
  }
  for (const split of splits) {
    if (!split.category_id) {
      errors.splits = 'Укажите категорию в каждой части разбивки';
      return errors;
    }
    if (!Number(split.amount) || Number(split.amount) <= 0) {
      errors.splits = 'Укажите сумму больше нуля в каждой части разбивки';
      return errors;
    }
  }
  // PERS2-83 AC-3: каждая часть — прямая дочерняя категория основной
  // категории (проверяется по справочнику пользователя, если он передан).
  if (Array.isArray(categories) && categories.length) {
    const headerId = String(formData.category_id);
    for (const split of splits) {
      const category = categories.find((item) => String(item.id) === String(split.category_id));
      const isChildOfHeader = Boolean(category && category.parent_id && String(category.parent_id) === headerId);
      if (!isChildOfHeader) {
        errors.splits = 'Категория части должна быть дочерней категорией основной категории';
        return errors;
      }
    }
  }
  if (Math.abs(splitTotal(splits) - Number(formData.amount)) > SPLIT_SUM_TOLERANCE) {
    errors.splits = 'Сумма частей должна равняться сумме операции';
  }
  return errors;
};

export const transactionFieldErrors = (formData, categories = []) => {
  const errors = {};
  if (!Number(formData.amount) || Number(formData.amount) <= 0) errors.amount = 'Укажите сумму больше нуля';
  if (!formData.date) {
    errors.date = 'Укажите дату операции';
  } else {
    // PERS2-79: время обязательно; без времени (date-only) — отдельное
    // сообщение, невалидный формат — своё.
    const dateValue = String(formData.date).trim();
    if (DATE_ONLY_RE.test(dateValue)) {
      errors.date = 'Укажите время операции (ЧЧ:ММ)';
    } else if (!DATE_TIME_RE.test(dateValue)) {
      errors.date = 'Проверьте формат даты и времени (ГГГГ-ММ-ДД ЧЧ:ММ)';
    }
  }

  const splitErrors = splitValidationErrors(formData, categories);
  const hasSplitsAttempt = Array.isArray(formData.splits) && formData.splits.length > 0;

  if (formData.type === 'expense') {
    // Без разбивки заголовочная категория обязательна (как раньше); при
    // разбивке её обязательность проверяет splitValidationErrors (PERS2-83).
    if (!hasSplitsAttempt && !formData.category_id) errors.category_id = 'Укажите категорию расхода';
    if (!formData.from_location_id) errors.from_location_id = 'Укажите счёт списания';
  }

  if (formData.type === 'income') {
    if (!formData.income_source_id) errors.income_source_id = 'Укажите источник дохода';
    if (!formData.to_location_id) errors.to_location_id = 'Укажите счёт зачисления';
  }

  if (formData.type === 'transfer') {
    if (!formData.from_location_id) errors.from_location_id = 'Укажите счёт списания';
    if (!formData.to_location_id) errors.to_location_id = 'Укажите счёт зачисления';
    if (!Number(formData.transfer_amount) || Number(formData.transfer_amount) <= 0) errors.transfer_amount = 'Укажите сумму зачисления больше нуля';
  }

  if (splitErrors.splits) errors.splits = splitErrors.splits;

  return errors;
};

export const clearTransactionFieldError = (errors, field) => {
  if (!errors[field]) return errors;
  const next = { ...errors };
  delete next[field];
  return next;
};

const SPLIT_ERROR_MESSAGES = [
  'Сумма частей должна равняться сумме операции',
  'Разбивка доступна только для расходов',
  'Укажите категорию в каждой части разбивки',
  'Укажите сумму больше нуля в каждой части разбивки',
  'Добавьте минимум 2 части разбивки или удалите разбивку',
  // PERS2-83: иерархия «часть — дочерняя основной категории».
  'При разбивке укажите основную категорию расхода',
  'Категория части должна быть дочерней категорией основной категории'
];

export const fieldErrorFromTransactionResponse = (message) => {
  if (SPLIT_ERROR_MESSAGES.includes(message)) return { splits: message };
  // PERS2-79: 400 бэкенда по формату даты → ошибка поля date.
  if (typeof message === 'string' && message.startsWith('Invalid date format')) {
    return { date: 'Проверьте формат даты и времени (ГГГГ-ММ-ДД ЧЧ:ММ)' };
  }
  return {
    'Category is required for expense transactions': { category_id: 'Укажите категорию расхода' },
    'Укажите категорию расхода': { category_id: 'Укажите категорию расхода' },
    'Income source is required for income transactions': { income_source_id: 'Укажите источник дохода' },
    'Укажите источник дохода': { income_source_id: 'Укажите источник дохода' },
    'From location is required for expense transactions': { from_location_id: 'Укажите счёт списания' },
    'Укажите счёт списания для расхода': { from_location_id: 'Укажите счёт списания' },
    'To location is required for income transactions': { to_location_id: 'Укажите счёт зачисления' },
    'Укажите счёт зачисления для дохода': { to_location_id: 'Укажите счёт зачисления' },
    'Both from and to locations are required for transfer transactions': {
      from_location_id: 'Укажите счёт списания',
      to_location_id: 'Укажите счёт зачисления'
    },
    'Укажите счета списания и зачисления для перевода': {
      from_location_id: 'Укажите счёт списания',
      to_location_id: 'Укажите счёт зачисления'
    }
  }[message] || {};
};
